'use client';

import { Badge } from '@shared/components/ui/badge';
import { cn } from '@shared/utils/utils';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo, useState } from 'react';

export interface TagsInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * TagsInput - Input de etiquetas visual
 *
 * Permite añadir etiquetas pulsando Enter. Las etiquetas se muestran como chips
 * que se pueden eliminar con un clic en la X. Las chips se alinean a la izquierda
 * y el cursor queda a la derecha (orden lectura natural).
 */
export const TagsInput = React.forwardRef<HTMLInputElement, TagsInputProps>(
  ({ value, onChange, placeholder, className, disabled }, ref) => {
    const tags = useMemo(() => value ?? [], [value]);
    const [inputValue, setInputValue] = useState('');

    const addTag = useCallback(
      (tag: string) => {
        const trimmed = tag.trim();
        if (trimmed && !tags.includes(trimmed)) {
          onChange([...tags, trimmed]);
        }
      },
      [tags, onChange],
    );

    const removeTag = useCallback(
      (index: number) => {
        onChange(tags.filter((_, i) => i !== index));
      },
      [tags, onChange],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addTag(inputValue);
          setInputValue('');
        } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
          removeTag(tags.length - 1);
        }
      },
      [inputValue, tags, addTag, removeTag],
    );

    return (
      <div
        className={cn(
          'flex flex-wrap items-center justify-start gap-2 min-h-9 px-3 py-1.5 rounded-md border shadow-sm',
          'border-border bg-muted/50 text-foreground',
          'focus-within:ring-2 focus-within:ring-ring focus-within:border-ring',
          'transition-colors',
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
      >
        {tags.map((tag, index) => (
          <Badge
            key={`${tag}-${index}`}
            variant="secondary"
            className="gap-1 pr-1 py-0.5 h-6 text-xs font-normal bg-muted/80 border-border shrink-0"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(index)}
              disabled={disabled}
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted transition-colors"
              aria-label="Eliminar etiqueta"
            >
              <FontAwesomeIcon icon={faXmark} className="size-2.5" />
            </button>
          </Badge>
        ))}
        <input
          ref={ref}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ''}
          disabled={disabled}
          className="min-w-[120px] flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground py-0 text-left"
        />
      </div>
    );
  },
);

TagsInput.displayName = 'TagsInput';
